import { FormEvent, useState } from "react";
import { AuthLayout } from "./AuthLayout";

interface SignUpProps {
  onSignUp: (email: string, password: string) => Promise<void>;
  onSwitchToSignIn: () => void;
  isLoading: boolean;
  error: string | null;
  onClearError: () => void;
}

export interface PasswordRules {
  minLength: boolean;
  hasUpper: boolean;
  hasLower: boolean;
  hasNumber: boolean;
  hasSpecial: boolean;
}

export function checkPasswordRules(pwd: string): PasswordRules {
  return {
    minLength: pwd.length >= 12,
    hasUpper: /[A-Z]/.test(pwd),
    hasLower: /[a-z]/.test(pwd),
    hasNumber: /[0-9]/.test(pwd),
    hasSpecial: /[^A-Za-z0-9]/.test(pwd),
  };
}

export function isPasswordValid(pwd: string): boolean {
  const r = checkPasswordRules(pwd);
  return r.minLength && r.hasUpper && r.hasLower && r.hasNumber && r.hasSpecial;
}

const PASSWORD_RULE_LABELS: { key: keyof PasswordRules; label: string }[] = [
  { key: "minLength", label: "At least 12 characters" },
  { key: "hasUpper",  label: "At least one uppercase letter" },
  { key: "hasLower",  label: "At least one lowercase letter" },
  { key: "hasNumber", label: "At least one number" },
  { key: "hasSpecial", label: "At least one special character" },
];

interface PasswordRequirementsProps {
  password: string;
}

export function PasswordRequirements({ password }: PasswordRequirementsProps) {
  const rules = checkPasswordRules(password);
  return (
    <ul className="mt-2 space-y-0.5">
      {PASSWORD_RULE_LABELS.map(({ key, label }) => {
        const passing = rules[key];
        return (
          <li key={key} className="flex items-center gap-1.5">
            {passing ? (
              <svg className="w-3 h-3 text-emerald-400 shrink-0" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg className="w-3 h-3 text-red-500 shrink-0" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
              </svg>
            )}
            <span className={`text-[11px] ${passing ? "text-emerald-400" : "text-slate-500"}`}>{label}</span>
          </li>
        );
      })}
    </ul>
  );
}

export function SignUp({ onSignUp, onSwitchToSignIn, isLoading, error, onClearError }: SignUpProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{ email?: string; password?: string; confirm?: string }>({});

  function validate(): boolean {
    const errors: typeof fieldErrors = {};
    if (!email.trim()) errors.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors.email = "Enter a valid email address";
    if (!password) errors.password = "Password is required";
    else if (!isPasswordValid(password)) errors.password = "Password does not meet all requirements";
    if (!confirm) errors.confirm = "Please confirm your password";
    else if (confirm !== password) errors.confirm = "Passwords do not match";
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    onClearError();
    try {
      await onSignUp(email, password);
    } catch {
      // error managed by parent
    }
  }

  const inputClass = (hasError?: string) => [
    "w-full pl-10 pr-4 py-2.5 bg-[#070d1a] border rounded-xl text-slate-100",
    "placeholder-slate-700 text-sm",
    "focus:outline-none focus:ring-1 focus:border-brand-600/50 focus:ring-brand-600/30",
    "disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200",
    hasError ? "border-red-700/60" : "border-[#1a2640] hover:border-[#263350]",
  ].join(" ");

  return (
    <AuthLayout>
      <div className="relative w-full max-w-md animate-fade-in">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-brand-700/20 border border-brand-600/25 mb-5 relative">
            <div className="absolute inset-0 rounded-2xl bg-brand-500/10 blur-md" />
            <svg className="relative w-8 h-8 text-brand-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-white tracking-tight">Create your account</h1>
          <p className="mt-1.5 text-sm text-slate-500">Competitive Intelligence Platform</p>
        </div>

        <div className="relative p-px rounded-2xl bg-gradient-to-br from-brand-700/40 via-[#1a2640] to-[#22d3ee]/20">
          <div className="bg-[#0d1424] rounded-2xl p-8 shadow-card-lg">
            <h2 className="text-sm font-semibold text-slate-300 mb-6 flex items-center gap-2">
              <span className="w-4 h-px bg-gradient-to-r from-brand-500 to-transparent" />
              New account
              <span className="flex-1 h-px bg-gradient-to-l from-[#1a2640] to-transparent" />
            </h2>

            {error && (
              <div className="mb-5 flex items-start gap-3 bg-red-950/30 border border-red-700/40 rounded-xl p-3.5 animate-fade-in">
                <svg className="w-4 h-4 text-red-400 mt-0.5 shrink-0" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
                </svg>
                <p className="text-xs text-red-300 leading-relaxed">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} noValidate className="space-y-4">
              {/* Email */}
              <div>
                <label htmlFor="reg-email" className="block text-[11px] font-semibold text-slate-500 uppercase tracking-widest mb-2">
                  Work email
                </label>
                <div className="relative">
                  <div className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none">
                    <svg className="w-3.5 h-3.5 text-slate-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                    </svg>
                  </div>
                  <input
                    id="reg-email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); if (fieldErrors.email) setFieldErrors((p) => ({ ...p, email: undefined })); }}
                    disabled={isLoading}
                    placeholder="you@company.com"
                    className={inputClass(fieldErrors.email)}
                  />
                </div>
                {fieldErrors.email && <p className="mt-1.5 text-[11px] text-red-400">{fieldErrors.email}</p>}
              </div>

              {/* Password */}
              <div>
                <label htmlFor="reg-password" className="block text-[11px] font-semibold text-slate-500 uppercase tracking-widest mb-2">
                  Password
                </label>
                <div className="relative">
                  <div className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none">
                    <svg className="w-3.5 h-3.5 text-slate-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                    </svg>
                  </div>
                  <input
                    id="reg-password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (!passwordTouched) setPasswordTouched(true);
                      if (fieldErrors.password) setFieldErrors((p) => ({ ...p, password: undefined }));
                    }}
                    onBlur={() => setPasswordTouched(true)}
                    disabled={isLoading}
                    placeholder="Min. 12 characters"
                    className={inputClass(fieldErrors.password) + " pr-10"}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    tabIndex={-1}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-600 hover:text-slate-400 transition-colors cursor-pointer"
                  >
                    {showPassword ? (
                      <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                      </svg>
                    ) : (
                      <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    )}
                  </button>
                </div>
                {/* Live password requirements checklist */}
                {passwordTouched && password && <PasswordRequirements password={password} />}
                {fieldErrors.password && !password && <p className="mt-1.5 text-[11px] text-red-400">{fieldErrors.password}</p>}
              </div>

              {/* Confirm */}
              <div>
                <label htmlFor="reg-confirm" className="block text-[11px] font-semibold text-slate-500 uppercase tracking-widest mb-2">
                  Confirm password
                </label>
                <div className="relative">
                  <div className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none">
                    <svg className="w-3.5 h-3.5 text-slate-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <input
                    id="reg-confirm"
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    value={confirm}
                    onChange={(e) => { setConfirm(e.target.value); if (fieldErrors.confirm) setFieldErrors((p) => ({ ...p, confirm: undefined })); }}
                    disabled={isLoading}
                    placeholder="Re-enter password"
                    className={inputClass(fieldErrors.confirm)}
                  />
                </div>
                {fieldErrors.confirm && <p className="mt-1.5 text-[11px] text-red-400">{fieldErrors.confirm}</p>}
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 px-4 mt-2 bg-gradient-to-r from-brand-600 to-brand-500 hover:from-brand-500 hover:to-brand-400 text-white text-sm font-semibold rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500/50 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center gap-2 shadow-glow-brand-sm hover:shadow-glow-brand cursor-pointer"
              >
                {isLoading ? (
                  <>
                    <div className="relative w-4 h-4">
                      <div className="absolute inset-0 rounded-full border-2 border-white/20" />
                      <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-white animate-spin" />
                    </div>
                    Creating account…
                  </>
                ) : (
                  <>
                    Create account
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                    </svg>
                  </>
                )}
              </button>
            </form>

            <p className="mt-5 text-center text-[11px] text-slate-600">
              Already have an account?{" "}
              <button
                onClick={onSwitchToSignIn}
                className="text-brand-400 hover:text-brand-300 transition-colors cursor-pointer"
              >
                Sign in
              </button>
            </p>
          </div>
        </div>
      </div>
    </AuthLayout>
  );
}
